import type { ManagedShow, ShowTableRequest, VendorParticipation } from "@/hooks/useShowOperations";

const stateLabels: Record<ManagedShow["state"], string> = {
  draft: "Draft",
  published: "Published",
  canceled: "Canceled",
};

const phaseLabels: Record<ManagedShow["phase"], string> = {
  upcoming: "Upcoming",
  live: "Live",
  completed: "Completed",
};

export function showStateLabel(state: ManagedShow["state"]): string {
  return stateLabels[state];
}

export function showPhaseLabel(phase: ManagedShow["phase"]): string {
  return phaseLabels[phase];
}

/**
 * A Show's state and phase in one line, for a place with room for one.
 *
 * Canceled stands alone: once a Show is called off, its phase reports only where the calendar got
 * to, and "Canceled · Upcoming" reads as a contradiction.
 */
export function showStatusLabel(show: Pick<ManagedShow, "state" | "phase">): string {
  return show.state === "canceled"
    ? showStateLabel(show.state)
    : `${showStateLabel(show.state)} · ${showPhaseLabel(show.phase)}`;
}

export function showScheduleLabel(show: Pick<ManagedShow, "sessions">): string {
  const sessions = show.sessions ?? [];
  if (!sessions.length) return "Schedule not set";
  const starts = new Date(sessions[0].starts_at);
  const ends = new Date(sessions[sessions.length - 1].ends_at);
  if (Number.isNaN(starts.valueOf()) || Number.isNaN(ends.valueOf())) return "Schedule not set";
  const format = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const start = format.format(starts);
  const end = format.format(ends);
  return start === end ? start : `${start}–${end}`;
}

/**
 * The same span as `showScheduleLabel`, with the hours and in the Show's own timezone.
 *
 * For a reader deciding whether they can get there, which a date alone does not answer. The
 * shorter label is for a row in a list, where the times would not fit.
 */
export function showScheduleDetails(show: Pick<ManagedShow, "sessions" | "timezone">): string {
  const sessions = show.sessions ?? [];
  if (!sessions.length) return "Schedule not set";
  const starts = new Date(sessions[0].starts_at);
  const ends = new Date(sessions[sessions.length - 1].ends_at);
  if (Number.isNaN(starts.valueOf()) || Number.isNaN(ends.valueOf())) return "Schedule not set";
  const format = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: show.timezone ?? undefined,
  });
  return `${format.format(starts)} – ${format.format(ends)}`;
}

export function showVenueLabel(show: Pick<ManagedShow, "venue_name">): string {
  return show.venue_name?.trim() || "Venue not set";
}

export function showSetupLabel(show: Pick<ManagedShow, "sessions" | "venue_name">): string {
  const missing = [
    ...(show.sessions?.length ? [] : ["schedule"]),
    ...(show.venue_name?.trim() ? [] : ["venue"]),
  ];
  return missing.length ? `Add ${missing.join(" and ")}` : "Schedule and venue ready";
}

export function formatShowMoney(amount: string, currency: string | null): string {
  if (!currency) return amount;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(Number(amount));
  } catch {
    return `${currency} ${amount}`;
  }
}

export function paymentRequestIds(
  requests: ShowTableRequest[],
  participationIds: Iterable<string>,
  paymentStatus: "due" | "paid",
): string[] {
  const selected = new Set(participationIds);
  return requests
    .filter((request) =>
      selected.has(request.participation_id)
      && request.status === "approved"
      && request.payment_status === paymentStatus
    )
    .map((request) => request.public_id);
}

export type StockGroupPublicationState = {
  published: boolean;
  searchable: boolean;
  label: string;
  detail: string;
};

export function stockGroupPublicationState(
  show: Pick<ManagedShow, "state" | "phase"> | null,
  participation: Pick<VendorParticipation, "status"> | null,
  stockGroupCount: number,
): StockGroupPublicationState {
  if (stockGroupCount === 0) {
    return {
      published: false,
      searchable: false,
      label: "Not published",
      detail: "No Location inventory is public for this Show.",
    };
  }

  const unavailable = show?.state !== "published"
    ? "The Show must be published."
    : show.phase === "completed"
    ? "This Show has ended."
    : participation?.status !== "confirmed"
    ? "Your Vendor participation must be confirmed."
    : null;

  return unavailable
    ? {
      published: true,
      searchable: false,
      label: "Published · not searchable",
      detail: unavailable,
    }
    : {
      published: true,
      searchable: true,
      label: "Published · searchable",
      detail: "Customers can find eligible cards from these Locations in this Show.",
    };
}

/** The two views of a hosted Show list. */
export type HostedShowFilter = "active" | "closed";

export const HOSTED_SHOW_FILTERS: { key: HostedShowFilter; label: string; }[] = [
  { key: "active", label: "Active" },
  { key: "closed", label: "Closed" },
];

type ListedShow = Pick<
  ManagedShow,
  "public_id" | "name" | "state" | "phase" | "sessions" | "venue_name"
>;

/** The Show's earliest session start, or `null` for a Show with no schedule yet. */
function showStart(show: Pick<ManagedShow, "sessions">): number | null {
  const starts = (show.sessions ?? [])
    .map((session) => new Date(session.starts_at).valueOf())
    .filter((value) => !Number.isNaN(value));
  return starts.length ? Math.min(...starts) : null;
}

/** Closed: the Show has already happened, or it was called off and is not going to. */
function isFinished(show: Pick<ManagedShow, "state" | "phase">): boolean {
  return show.phase === "completed" || show.state === "canceled";
}

/** Case-insensitive match on the two things an Organizer would type: the name and the venue. */
function matchesSearch(show: ListedShow, needle: string): boolean {
  if (!needle) return true;
  return `${show.name} ${show.venue_name ?? ""}`.toLowerCase().includes(needle);
}

/**
 * Every managed Show split into Active and Closed, sorted by when it happens.
 *
 * Active is everything still ahead of the Organizer, Drafts included, because a Draft for next
 * weekend is the work in front of them and each row already states its own state. Closed covers
 * both ways a Show ends: it happened, or it was called off and never will.
 *
 * Active runs soonest first, with an unscheduled Show last because it cannot be compared to a
 * dated one; Closed runs most recent first. Name then public id break ties, so the order is total.
 */
export function groupHostedShows<T extends ListedShow>(
  shows: readonly T[],
  search = "",
): Record<HostedShowFilter, T[]> {
  const needle = search.trim().toLowerCase();
  const groups: Record<HostedShowFilter, T[]> = { active: [], closed: [] };
  for (const show of shows) {
    if (!matchesSearch(show, needle)) continue;
    groups[isFinished(show) ? "closed" : "active"].push(show);
  }
  for (const [key, group] of Object.entries(groups)) {
    const newestFirst = key === "closed";
    group.sort((a, b) => {
      const left = showStart(a);
      const right = showStart(b);
      if (left !== right) {
        if (left === null) return 1;
        if (right === null) return -1;
        return newestFirst ? right - left : left - right;
      }
      return a.name.localeCompare(b.name) || a.public_id.localeCompare(b.public_id);
    });
  }
  return groups;
}
